import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaEye } from "react-icons/fa";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import Grid from '../components/Grid';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ReceivedStockOnLoan({ onAddReceivedStockOnLoanClick }) {
  const [transferOrders, setTransferOrders] = useState([]);
  const [selectedTransferOrder, setSelectedTransferOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
  
        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        setUserRole(userResponse.data.role); // Set the user role
      } catch (error) {
        console.error("Error fetching user role:", error);
        toast.error("Error fetching user role.");
      }
    };
  
    fetchUserRole();
  }, []);

  useEffect(() => {
    const fetchTransferOrders = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/transferOrders`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const transferOrders = response.data.transferOrders || [];

        const formattedTransferOrders = transferOrders.map((order) => ({
          id: order._id,
          transferOrderCode: order.transfer_order_code || "N/A",
          fromLocation: order.from_location_id?.location_name || "Unknown",
          toLocation: order.to_location_id?.location_name || "Unknown",
          transferOrderDate : new Date(order.transfer_order_date).toLocaleDateString("en-GB"),
          created_by: order.created_by?.username || "Unknown",
          status: order.status,
          createdAt: order.createdAt,
          originalOrder: order, // Store the original data for details view
        }));

        setTransferOrders(formattedTransferOrders);
      } catch (error) {
        console.error("Error fetching transfer orders:", error);
        toast.error("Error fetching transfer orders.");
      }
    };

    console.log("Fetching transfer orders: ", transferOrders);

    fetchTransferOrders();
  }, []);

  const handleTransferOrderClick = async (transferOrder) => {

    console.log("Transfer Order Clicked: ", transferOrder);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }
  
      const response = await axios.get(
        `${API_BASE_URL}/transferOrders/${transferOrder.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const detailedOrder = response.data.transferOrder;
      console.log("Detailed Transfer Order:", detailedOrder);
  
      const formattedProducts = detailedOrder.transferOrderItems.map((item) => ({
        id: item.product_id._id,
        product_name: item.product_id.product_name,
        product_image: item.product_id.product_image,
        product_code: item.product_id.product_code,
        quantity: item.qty,
        unit: item.product_id.unit?.unit_name,
      }));

      console.log("Selected Transfer Order:", formattedProducts);
  
      setSelectedTransferOrder({
        ...detailedOrder,
        products: formattedProducts,
        sourceLocation: detailedOrder.from_location_id.location_name,
        destinationLocation: detailedOrder.to_location_id.location_name,
        selectedProducts: formattedProducts || [],
      });
      
    } catch (error) {
      console.error("Error fetching transfer order details:", error.message);
      toast.error("Error fetching transfer order details.");
    }
  };

  const handleClosePopup = () => {
    setSelectedTransferOrder(null);
  };

  const sortedTransferOrders = [...transferOrders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const filteredTransferOrders = sortedTransferOrders.filter((order) =>
      order.transferOrderCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { field: "transferOrderCode", headerName: "Transfer Order Code", flex: 1 },
    { field: "fromLocation", headerName: "Source Location", flex: 1 },
    { field: "toLocation", headerName: "Destination Location", flex: 1 },
    { field: "transferOrderDate", headerName: "Date", flex: 1 },
    { field: "created_by", headerName: "Created By", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const status = params.row.status;
    
        // Map statuses to classes
        const statusClassMap = {
          "TO Created": "bg-blue-500 text-white",
          "Dispatched": "bg-green-500 text-white",
          "Drafted": "bg-lime-600 text-white",
        };
    
        // Get the class based on status or default to a neutral color
        const statusClass = statusClassMap[status] || "bg-gray-500 text-white"; // Default class if status not found
    
        return (
          <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <button
          onClick={() => handleTransferOrderClick(params.row)}
          className="bg-yellow-500 text-white p-2 flex items-center justify-center rounded"
          style={{ width: "30px", height: "30px" }}
        >
          <FaEye className="text-white" />
        </button>
      ),
    },
  ];

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-semibold">Transfer Orders</h1>
          {(userRole === "Company Admin" || userRole === "Farm User") && (
            <button
              onClick={onAddReceivedStockOnLoanClick}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg"
            >
              Add Transfer Order
            </button>
          )}
        </div>
        <div className="bg-white p-4 rounded-lg shadow mb-8">
          <div className="flex justify-between mb-4 items-center">
            <div className="relative">
              <input
                type="text"
                placeholder="Search Transfer Orders"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 pl-10 border border-gray-300 rounded-lg"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
              </div>
            </div>
          </div>
          <Grid rows={filteredTransferOrders} columns={columns} getRowId={(row) => row.transferOrderCode}/>
        </div>
        {selectedTransferOrder && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto">
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <h2 className="text-xl font-semibold mb-4">
                {selectedTransferOrder.transfer_order_code}
              </h2>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Source Location</label>
                <input
                  type="text"
                  value={selectedTransferOrder.from_location_id?.location_name || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Destination Location</label>
                <input
                  type="text"
                  value={selectedTransferOrder.to_location_id?.location_name || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Transfer Order Created Date</label>
                <input
                  type="text"
                  value={selectedTransferOrder.transfer_order_date || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Created By</label>
                <input
                  type="text"
                  value={selectedTransferOrder.created_by?.username || "Unknown"}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Status</label> 
                <input
                  type="text"
                  value={selectedTransferOrder.status || "Unknown"}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">
                    Production Batch Selected Products
                </h3>
                <div style={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={selectedTransferOrder?.products || []} // Use the products from selectedProductionBatch
                    columns={[
                      { field: "product_code", headerName: "ID", width: 100 },
                      {
                        field: "product_image",
                        headerName: "Image",
                        width: 150,
                        renderCell: (params) =>
                        params.row.product_image ? (
                          <img
                            src={`${process.env.REACT_APP_IMAGE_BASE_URL}${params.row.product_image}`}
                            alt="Product"
                            className="h-16 w-16 object-cover rounded-md"
                          />
                          ) : (
                            "No Image"
                          ),
                      },
                      {
                        field: "product_name",
                        headerName: "Product Name",
                        width: 150,
                      },
                      {
                        field: "quantity",
                        headerName: "Quantity",
                        width: 150,
                        headerAlign: "right",
                        align: "right"
                      },
                      { 
                          field: "unit", 
                          headerName: "Units", 
                          width: 150,                    
                          headerAlign: "center", 
                          align: "center" 
                        },
                    ]}
                    pageSize={5}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReceivedStockOnLoan;